import { supabase } from "@/lib/supabase";

export type AiLang = "fr" | "nl" | "en";

export interface AiResult {
  titre: string;
  description: string;
}

/**
 * Génère titre + description via l'Edge Function `generate-bien-description`
 * (mode "generate"). L'Edge fetch le bien + son quartier en service-role,
 * compose un prompt complet et appelle Claude. Auth = JWT supabase courant.
 */
export async function generateBienDescription(args: {
  bienId: string;
  lang: AiLang;
}): Promise<AiResult> {
  const { data, error } = await supabase.functions.invoke<AiResult>("generate-bien-description", {
    body: { mode: "generate", bienId: args.bienId, lang: args.lang },
  });
  if (error) throw new Error(error.message ?? "generateBienDescription failed");
  if (!data || typeof data.titre !== "string" || typeof data.description !== "string") {
    throw new Error("Réponse IA invalide");
  }
  return data;
}

/**
 * Traduit titre + description d'une langue source vers une langue cible.
 * N'effectue aucun fetch DB côté Edge — pratique pour traduire ce que
 * l'utilisateur voit déjà à l'écran (état local, possiblement non sauvegardé).
 */
export async function translateBienDescription(args: {
  lang: AiLang;
  sourceLang: AiLang;
  sourceTitre: string;
  sourceDescription: string;
}): Promise<AiResult> {
  const { data, error } = await supabase.functions.invoke<AiResult>("generate-bien-description", {
    body: {
      mode: "translate",
      lang: args.lang,
      sourceLang: args.sourceLang,
      sourceTitre: args.sourceTitre,
      sourceDescription: args.sourceDescription,
    },
  });
  if (error) throw new Error(error.message ?? "translateBienDescription failed");
  if (!data || typeof data.titre !== "string" || typeof data.description !== "string") {
    throw new Error("Réponse IA invalide");
  }
  return data;
}

export interface QuartierTranslateResult {
  description: string;
  pointsInteret: string;
}

/**
 * Traduit la description + les points d'intérêt d'un quartier d'une langue
 * source vers une langue cible. Mêmes garanties que translateBienDescription.
 */
export async function translateQuartier(args: {
  lang: AiLang;
  sourceLang: AiLang;
  sourceDescription: string;
  sourcePointsInteret: string;
}): Promise<QuartierTranslateResult> {
  const { data, error } = await supabase.functions.invoke<QuartierTranslateResult>("generate-bien-description", {
    body: {
      mode: "translate-quartier",
      lang: args.lang,
      sourceLang: args.sourceLang,
      sourceDescription: args.sourceDescription,
      sourcePointsInteret: args.sourcePointsInteret,
    },
  });
  if (error) throw new Error(error.message ?? "translateQuartier failed");
  if (!data || typeof data.description !== "string" || typeof data.pointsInteret !== "string") {
    throw new Error("Réponse IA invalide");
  }
  return data;
}
